/**
 * Correctly rounded natural log, exp and expm1: the double nearest the true value.
 *
 * LSEG's Adfin runs on the Microsoft C runtime, whose log and exp give the
 * nearest double on every value the IM:LOG goldens use, where V8's Math.log and
 * Math.exp are off by one unit in the last place on some of them (Math.log(3),
 * Math.exp(-0.34657359027997264), ...): enough to change the 15th decimal of
 * an EAV.RDP.INTERP result (task-4-report.md). Each is computed here in
 * double-double arithmetic (about 106 bits) and rounded once.
 */

type DD = [number, number]; // hi + lo, |lo| <= half an ulp of hi

const LN2_HI = 0.6931471805599453; // Math.LN2
const LN2_LO = 2.3190468138462996e-17; // ln 2 - LN2_HI

function quickTwoSum(a: number, b: number): DD {
  const s = a + b;
  return [s, b - (s - a)];
}

function twoSum(a: number, b: number): DD {
  const s = a + b;
  const bb = s - a;
  return [s, a - (s - bb) + (b - bb)];
}

/** Veltkamp's split of a double into two 26-bit halves. */
function split(a: number): DD {
  const c = 134217729 * a; // 2^27 + 1
  const hi = c - (c - a);
  return [hi, a - hi];
}

/** a * b exactly, as a double-double (Dekker). */
function twoProd(a: number, b: number): DD {
  const p = a * b;
  const [ah, al] = split(a);
  const [bh, bl] = split(b);
  return [p, ah * bh - p + ah * bl + al * bh + al * bl];
}

function add(a: DD, b: DD): DD {
  const [s, e] = twoSum(a[0], b[0]);
  const [t, f] = twoSum(a[1], b[1]);
  const [s2, e2] = quickTwoSum(s, e + t);
  return quickTwoSum(s2, e2 + f);
}

function mul(a: DD, b: DD): DD {
  const [p, e] = twoProd(a[0], b[0]);
  return quickTwoSum(p, e + (a[0] * b[1] + a[1] * b[0]));
}

function divByInt(a: DD, n: number): DD {
  const q1 = a[0] / n;
  const [p, e] = twoProd(q1, n);
  const q2 = (a[0] - p - e + a[1]) / n;
  return quickTwoSum(q1, q2);
}

/**
 * a = k ln 2 + r, and e^r - 1 as a double-double, for |a| < 700: e^(r / 1024) - 1
 * by its Taylor series, squared up ten times as (1 + m)^2 - 1 = 2m + m^2.
 */
function expParts(a: number): { k: number; m: DD } {
  const k = Math.round(a / Math.LN2);
  const [p, pe] = twoProd(k, LN2_HI);
  const [r0, r1] = twoSum(a, -p);
  let r = quickTwoSum(r0, r1 - pe - k * LN2_LO);
  r = [r[0] / 1024, r[1] / 1024];
  let t: DD = [1, 0];
  for (let n = 12; n >= 2; n--) t = add([1, 0], divByInt(mul(r, t), n));
  let m = mul(r, t);
  for (let i = 0; i < 10; i++) m = add([2 * m[0], 2 * m[1]], mul(m, m));
  return { k, m };
}

/** e^a as a double-double, for |a| < 700. */
function expDD(a: number): DD {
  const { k, m } = expParts(a);
  const [hi, lo] = add([1, 0], m);
  const scale = Math.pow(2, k);
  return [hi * scale, lo * scale];
}

/** e^a, rounded to the nearest double. */
export function crExp(a: number): number {
  if (!(Math.abs(a) < 700)) return Math.exp(a);
  const [hi, lo] = expDD(a);
  return hi + lo;
}

/**
 * e^a - 1, rounded to the nearest double (the C library's expm1, which
 * tanh is built on). Near 0 (k = 0) it is e^r - 1 itself, with no 1 to lose.
 */
export function crExpm1(a: number): number {
  if (!(Math.abs(a) < 700)) return Math.expm1(a);
  const { k, m } = expParts(a);
  if (k === 0) return m[0] + m[1];
  const [hi, lo] = expDD(a);
  const [d, e] = twoSum(hi, -1);
  return d + (e + lo);
}

/**
 * ln x, rounded to the nearest double: Math.log's value y, corrected by one
 * Newton step in double-double, y + (x - e^y) / e^y.
 */
export function crLog(x: number): number {
  const y = Math.log(x);
  if (!(x > 0) || !Number.isFinite(x) || !(Math.abs(y) < 700)) return y;
  const [eh, el] = expDD(y);
  const [d, de] = twoSum(x, -eh);
  return y + (d + (de - el)) / eh;
}
